/**
 * Opt-in switches for work that has shipped but is still settling.
 *
 * Stored in localStorage for the same reason the profile window settings are:
 * the AngularJS shell reads them synchronously while a route is still resolving,
 * so an async source would answer too late. It is a per-browser preference, not
 * an agency setting — turning a beta feature off affects only the person who
 * turned it off.
 *
 * The beta profile window is deliberately NOT stored here. It keeps living in
 * `profileWindowSettings.store`, next to the four options that only describe it,
 * so that turning it on and off from the Beta features modal and configuring it
 * from the profile window settings modal read and write one value. Only the
 * checkbox moved.
 */

export type BetaFeatures = {
  /**
   * Caregivers are read one page at a time from an endpoint, instead of the
   * whole agency's caregivers being loaded into the browser as a dictionary and
   * joined against there. Off, the caregivers page loads that dictionary again.
   */
  caregiverMemoryImprovements: boolean;
  /**
   * The same for patients: the list asks the server for the page it is showing
   * rather than the browser holding every patient in the agency.
   */
  patientMemoryImprovements: boolean;
  /**
   * The AI mode on the global search bar: typing a request in words plans and
   * runs the navigation and filtering it needs. Off unless asked for, unlike the
   * two above - it is new, it sends the request to a model outside this system,
   * and nothing else in the app depends on it being on.
   */
  aiSearch: boolean;
};

/**
 * Off by default. Every one of these is opt-in: the pages they switch on are
 * still settling, and some of what they replace - the Angular caregivers list,
 * the compliance page - is what an agency uses every day. Somebody who has
 * never opened this modal gets the app as it was.
 */
export const defaultBetaFeatures: BetaFeatures = {
  caregiverMemoryImprovements: false,
  patientMemoryImprovements: false,
  aiSearch: false,
};

const STORAGE_KEY = "medflyt:beta-features";

/** Fired on same-tab writes; cross-tab writes arrive as a native `storage` event. */
const CHANGE_EVENT = "medflyt:beta-features-change";

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function coerceBoolean(value: unknown, fallback: boolean): boolean {
  return typeof value === "boolean" ? value : fallback;
}

function parseBetaFeatures(value: unknown): BetaFeatures {
  if (!isRecord(value)) {
    return defaultBetaFeatures;
  }

  // The two switches started life as one. Anyone still carrying the old value
  // keeps it, applied to both halves; this can go once nobody has it stored.
  const legacyBoth = coerceBoolean(value.caregiverPatientMemoryImprovements, true);

  return {
    caregiverMemoryImprovements: coerceBoolean(value.caregiverMemoryImprovements, legacyBoth),
    patientMemoryImprovements: coerceBoolean(value.patientMemoryImprovements, legacyBoth),
    // Not part of the legacy pair: anyone with a snapshot from before this
    // existed gets it off, which is its default anyway.
    aiSearch: coerceBoolean(value.aiSearch, defaultBetaFeatures.aiSearch),
  };
}

/**
 * `useSyncExternalStore` compares snapshots by identity, so parsing storage on
 * every call would loop forever. The parsed value is cached against the raw
 * string it came from and only re-parsed when that string actually changes.
 */
let cachedRaw: string | null | undefined;
let cachedFeatures: BetaFeatures = defaultBetaFeatures;

export function getBetaFeatures(): BetaFeatures {
  let raw: string | null = null;

  try {
    raw = window.localStorage.getItem(STORAGE_KEY);
  } catch {
    // Unreadable storage falls back to defaults rather than breaking the page
    // that is asking.
    return defaultBetaFeatures;
  }

  if (raw !== cachedRaw) {
    cachedRaw = raw;

    try {
      cachedFeatures = parseBetaFeatures(raw === null ? undefined : JSON.parse(raw));
    } catch {
      cachedFeatures = defaultBetaFeatures;
    }
  }

  return cachedFeatures;
}

export function setBetaFeatures(features: BetaFeatures): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(features));
  } catch {
    // Quota or private-mode failures are non-fatal: the setting just will not
    // survive the reload.
    return;
  }

  window.dispatchEvent(new CustomEvent(CHANGE_EVENT));
}

export function subscribeToBetaFeatures(onChange: () => void): () => void {
  const handleStorage = (event: StorageEvent) => {
    if (event.key === null || event.key === STORAGE_KEY) {
      onChange();
    }
  };

  window.addEventListener(CHANGE_EVENT, onChange);
  window.addEventListener("storage", handleStorage);

  return () => {
    window.removeEventListener(CHANGE_EVENT, onChange);
    window.removeEventListener("storage", handleStorage);
  };
}

/**
 * Their own exports because callers outside React - the AngularJS router, which
 * decides which caregivers page exists at all - ask these questions and should
 * not have to know the shape of the settings object.
 */
export function isCaregiverMemoryImprovementsEnabled(): boolean {
  return getBetaFeatures().caregiverMemoryImprovements;
}

export function isPatientMemoryImprovementsEnabled(): boolean {
  return getBetaFeatures().patientMemoryImprovements;
}

/**
 * Read by the AngularJS search bar, which decides whether the AI mode exists at
 * all - the toggle, the input and the capability manifest behind it.
 */
export function isAiSearchEnabled(): boolean {
  return getBetaFeatures().aiSearch;
}
